import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@/components/brand/Icon';
import { cn } from '@/lib/cn';

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const BADGE_SUBTLE: Record<Tone, string> = {
  success: 'bg-success-bg text-success-fg border-success-border',
  warning: 'bg-warning-bg text-warning-fg border-warning-border',
  danger: 'bg-danger-bg text-danger-fg border-danger-border',
  info: 'bg-info-bg text-info-fg border-info-border',
  neutral: 'bg-neutral-bg text-neutral-fg border-neutral-border',
};

const BADGE_DOT: Record<Tone, string> = {
  success: 'bg-success-solid',
  warning: 'bg-warning-solid',
  danger: 'bg-danger-solid',
  info: 'bg-info-solid',
  neutral: 'bg-neutral-solid',
};

const BADGE_SOLID: Record<Tone, string> = {
  success: 'border-success-solid bg-success-solid text-on-solid',
  warning: 'border-warning-solid bg-warning-solid text-on-solid',
  danger: 'border-danger-solid bg-danger-solid text-on-solid',
  info: 'border-info-solid bg-info-solid text-on-solid',
  neutral: 'border-neutral-solid bg-neutral-solid text-on-solid',
};

export function Badge({
  tone = 'neutral',
  solid,
  children,
  className,
}: {
  tone?: Tone | undefined;
  solid?: boolean | undefined;
  children: React.ReactNode;
  className?: string | undefined;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-xs border px-2 text-label font-semibold',
        solid ? BADGE_SOLID[tone] : BADGE_SUBTLE[tone],
        className,
      )}
    >
      <span
        className={cn('size-1.5 rounded-full', solid ? 'bg-on-solid' : BADGE_DOT[tone])}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

export function Chip({
  selected,
  children,
  className,
  ...rest
}: {
  selected?: boolean | undefined;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        'inline-flex h-10 items-center gap-1 rounded-sm border px-4 text-body-sm font-medium',
        'transition-colors duration-150 disabled:pointer-events-none disabled:opacity-45',
        selected
          ? 'border-inverse bg-inverse text-on-inverse'
          : 'border-border-default bg-surface text-primary hover:border-border-strong',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Card({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      className={cn('rounded-lg border border-border-subtle bg-surface shadow-card', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

const ALERT_ICON: Record<Tone, IconName> = {
  success: 'check-circle',
  warning: 'alert-triangle',
  danger: 'x-circle',
  info: 'info',
  neutral: 'info',
};

export function Alert({
  tone = 'info',
  title,
  children,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
}: {
  tone?: Tone | undefined;
  title?: string | undefined;
  children?: React.ReactNode | undefined;
  onDismiss?: (() => void) | undefined;
  dismissLabel?: string | undefined;
  className?: string | undefined;
}): React.JSX.Element {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-md border px-4.5 py-4', BADGE_SUBTLE[tone], className)}
    >
      <Icon name={ALERT_ICON[tone]} size={20} className="mt-px shrink-0" />
      <div className="flex flex-1 flex-col gap-0.5">
        {title && <p className="text-body-sm font-medium">{title}</p>}
        {children && <div className="text-body-sm text-secondary">{children}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-xs opacity-70 hover:opacity-100"
        >
          <Icon name="x" size={18} />
        </button>
      )}
    </div>
  );
}

const AVATAR_SIZES = {
  sm: 'size-8 text-caption',
  md: 'size-10 text-body-sm',
  lg: 'size-14 text-body',
  xl: 'size-24 text-h4',
};

export function Avatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name: string;
  src?: string | null | undefined;
  size?: keyof typeof AVATAR_SIZES | undefined;
  className?: string | undefined;
}): React.JSX.Element {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => [...part][0]?.toLocaleUpperCase('az-AZ') ?? '')
    .join('');

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        loading="lazy"
        className={cn('rounded-full object-cover', AVATAR_SIZES[size], className)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-midnight-raised font-semibold text-on-midnight',
        AVATAR_SIZES[size],
        className,
      )}
    >
      {initials}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-sm bg-border-subtle', className)}
    />
  );
}

export function AccordionItem({
  question,
  answer,
  defaultOpen = false,
}: {
  question: string;
  answer: string;
  defaultOpen?: boolean | undefined;
}): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-border-default">
      <h3>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-6 py-6 text-left"
        >
          <span className="text-h4 font-semibold text-primary">{question}</span>
          <Icon
            name="chevron-down"
            size={22}
            className={cn(
              'shrink-0 text-tertiary transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </button>
      </h3>
      <div hidden={!open} className="pb-6 pr-10">
        <p className="text-body text-secondary">{answer}</p>
      </div>
    </div>
  );
}

export function Section({
  midnight,
  subtle,
  className,
  children,
  ...rest
}: {
  midnight?: boolean | undefined;
  subtle?: boolean | undefined;
} & React.HTMLAttributes<HTMLElement>): React.JSX.Element {
  return (
    <section
      className={cn(
        'py-16 md:py-24 lg:py-28',
        midnight ? 'bg-midnight text-on-midnight' : subtle ? 'bg-subtle' : 'bg-canvas',
        className,
      )}
      {...rest}
    >
      <div className="mv-container">{children}</div>
    </section>
  );
}

export function SectionHeader({
  overline,
  title,
  midnight,
  action,
  className,
}: {
  overline: string;
  title: string;
  midnight?: boolean | undefined;
  action?: React.ReactNode | undefined;
  className?: string | undefined;
}): React.JSX.Element {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-6', className)}>
      <div className="flex max-w-[46rem] flex-col gap-3.5">
        <p
          className={cn(
            'text-overline font-semibold uppercase',
            midnight ? 'text-on-midnight-accent' : 'text-accent',
          )}
        >
          {overline}
        </p>
        <h2
          className={cn(
            'font-display text-h2 font-normal md:text-h1',
            midnight ? 'text-on-midnight' : 'text-primary',
          )}
        >
          {title}
        </h2>
      </div>
      {action}
    </div>
  );
}

export function ArrowLink({
  to,
  children,
  midnight,
  icon = 'arrow-right',
}: {
  to: string;
  children: React.ReactNode;
  midnight?: boolean | undefined;
  icon?: IconName | undefined;
}): React.JSX.Element {
  return (
    <Link
      to={to}
      className={cn(
        'group inline-flex items-center gap-2 pb-2.5 text-body font-medium',
        midnight ? 'text-on-midnight' : 'text-primary',
      )}
    >
      {children}
      <Icon
        name={icon}
        size={18}
        className="transition-transform duration-200 group-hover:translate-x-0.5"
      />
    </Link>
  );
}

export function EmptyState({
  icon = 'info',
  title,
  body,
  action,
  className,
}: {
  icon?: IconName | undefined;
  title: string;
  body?: string | undefined;
  action?: React.ReactNode | undefined;
  className?: string | undefined;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3.5 rounded-lg bg-subtle px-12 py-14 text-center',
        className,
      )}
    >
      <span className="flex size-16 items-center justify-center rounded-full bg-surface text-tertiary">
        <Icon name={icon} size={26} />
      </span>
      <p className="text-h4 font-semibold text-primary">{title}</p>
      {body && <p className="max-w-[34rem] text-body text-secondary">{body}</p>}
      {action}
    </div>
  );
}

export function Toast({
  message,
  actionLabel,
  onAction,
  onDismiss,
  icon = 'check-circle',
  duration = 5000,
}: {
  message: string;
  actionLabel?: string | undefined;
  onAction?: (() => void) | undefined;
  onDismiss: () => void;
  icon?: IconName | undefined;
  duration?: number | undefined;
}): React.JSX.Element {
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(timer);
  }, [message, duration, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed bottom-6 left-1/2 z-50 flex w-[min(26.25rem,calc(100vw-2rem))] -translate-x-1/2 items-center gap-3',
        'rounded-md bg-inverse px-4.5 py-4 shadow-overlay',
        'md:left-6 md:translate-x-0',
      )}
    >
      <Icon name={icon} size={20} className="shrink-0 text-on-midnight-accent" />
      <p className="flex-1 text-body-sm font-medium text-on-inverse">{message}</p>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="shrink-0 rounded-xs text-body-sm font-medium text-on-midnight-accent hover:underline"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
